import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { finalize } from 'rxjs';
import { EMicaChat_TipoMensaje } from '@core/enums/micachat.enum';
import { MicaChatApiService } from '@core/services/api/mica-chat-api.service';
import { EvolutionApiService, IEvolutionEnviarMensajeReq } from '@core/services/api/evolution-api.service';
import { MicaChatService } from '@shared/services/mica-chat.service';
import { ToastService } from '@shared/services/toast.service';
import { IMicaChatTelefonoData } from '@core/interfaces/entidades/telefono.entidad';

const EXTENSIONES_IMAGEN = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
const EXTENSIONES_VIDEO = ['mp4', 'webm', 'mov'];
const EXTENSIONES_AUDIO = ['mp3', 'wav', 'aac', 'ogg', 'oga'];
const EXTENSIONES_DOCUMENTO = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv'];

const TAMANO_MAX_MB = 50;

interface IAttachmentPreview {
  tipo: EMicaChat_TipoMensaje;
  file: File;
  blobUrl: string | null;
  fileName: string;
  fromRecording: boolean;
}

@Component({
  selector: 'app-mica-chat-input-mensaje',
  imports: [],
  templateUrl: './mica-chat-input-mensaje.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './mica-chat-input-mensaje.css',
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class MicaChatInputMensaje implements OnInit, OnDestroy {
  ngOnInit() {
    this.generateWaveform();
  }

  ngOnDestroy() {
    this.liberarRecursosGrabacion();
    this.revocarBlobUrl();
  }

  @ViewChild('composer') composerRef?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('emojiMenu') emojiMenuRef?: ElementRef<HTMLElement>;
  @ViewChild('fileInput') fileInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('audioPreview') audioPreviewRef?: ElementRef<HTMLAudioElement>;

  private readonly metaApi = inject(MicaChatApiService);
  private readonly evoApi = inject(EvolutionApiService);
  private readonly MicaChatService = inject(MicaChatService);
  private readonly toastService = inject(ToastService);

  readonly bars = signal<number[]>([]);
  generateWaveform() {
    const numberOfBars = 20;
    const newBars = Array.from({ length: numberOfBars }, () => Math.floor(Math.random() * 20) + 5);
    this.bars.set(newBars);
  }

  showEmojiPicker = signal(false);
  messageDraft = signal('');
  uploading = signal(false);
  uploadProgress = signal(0);
  isDragOver = signal(false);

  private typingTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastPresenceSent: 'composing' | 'paused' | null = null;
  private lastPresenceTime = 0;
  private lastWordCount = 0;
  private static readonly PRESENCIA_THROTTLE_MS = 2000;
  private static readonly PRESENCIA_TIMEOUT_MS = 4000;

  readonly recordingState = signal<'idle' | 'recording'>('idle');
  readonly tiempoGrabacion = signal(0);
  readonly attachmentPreview = signal<IAttachmentPreview | null>(null);
  readonly composerMaxHeightPx = 128;
  readonly maxDuracionGrabacion = 120;

  readonly emojis = ['😀', '😂', '😍', '😎', '🥳', '🙏', '👍', '🔥', '🎉', '✅', '💬', '📌'];

  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private grabacionInterval: ReturnType<typeof setInterval> | null = null;
  private audioMimeType = '';

  toggleEmojiPicker() {
    this.showEmojiPicker.update((value) => !value);
  }

  onDocumentClick(event: MouseEvent) {
    if (!this.showEmojiPicker()) return;
    const target = event.target as Node | null;
    const container = this.emojiMenuRef?.nativeElement;
    if (!target || !container) return;
    if (!container.contains(target)) {
      this.showEmojiPicker.set(false);
    }
  }

	onInput(textarea: HTMLTextAreaElement) {
		this.messageDraft.set(textarea.value);
		this.autoResizeTextarea(textarea);
		this.notificarEscribiendo();
	}

	private notificarEscribiendo() {
		const telefono = this.MicaChatService.getTelefonoSeleccionado();
		if (telefono?.proveedor !== 'NO_OFICIAL') return;
		const chat = this.MicaChatService.getChatSeleccionado();
		const remoteJid = chat?.remoteJid;
		if (!remoteJid) return;

		const ahora = Date.now();
		const texto = this.messageDraft().trim();
		const wordCount = texto.length === 0 ? 0 : texto.split(/\s+/).length;

		if (this.typingTimeout) {
			clearTimeout(this.typingTimeout);
		}

		const nuevaPalabra = wordCount > this.lastWordCount;
		const puedeEnviar = ahora - this.lastPresenceTime >= MicaChatInputMensaje.PRESENCIA_THROTTLE_MS;
		const debeEnviar = this.lastPresenceSent !== 'composing' || (nuevaPalabra && puedeEnviar);

		if (debeEnviar) {
			this.evoApi.enviarPresencia(telefono.id, remoteJid, 'composing').subscribe();
			this.lastPresenceSent = 'composing';
			this.lastPresenceTime = ahora;
		}

		this.lastWordCount = wordCount;

		this.typingTimeout = setTimeout(() => {
			if (telefono.proveedor === 'NO_OFICIAL' && remoteJid) {
				this.evoApi.enviarPresencia(telefono.id, remoteJid, 'paused').subscribe();
				this.lastPresenceSent = null;
			}
			this.typingTimeout = null;
		}, MicaChatInputMensaje.PRESENCIA_TIMEOUT_MS);
	}

  insertEmoji(emoji: string) {
    const textarea = this.getComposer();
    if (!textarea) return;
    this.insertAtCursor(textarea, emoji);
    this.onInput(textarea);
    textarea.focus();
  }

  sendMessage() {
    const telefono = this.MicaChatService.getTelefonoSeleccionado();
    const chatSeleccionado = this.MicaChatService.getChatSeleccionado();
    if (!telefono || !chatSeleccionado) return;
    const message = this.messageDraft().trim();
    if (!message) return;

    const para =
      telefono.proveedor === 'NO_OFICIAL'
        ? (chatSeleccionado.remoteJid || chatSeleccionado.numero_contacto)
        : chatSeleccionado.numero_contacto;

    if (telefono.proveedor === 'NO_OFICIAL') {
      const evoDto: IEvolutionEnviarMensajeReq = {
        remoteJid: para,
        texto: message,
        tipo: EMicaChat_TipoMensaje.TEXT,
      };
      this.evoApi.enviarMensaje(telefono.id, evoDto).subscribe({
        error: (error) => {
          this.toastService.show('top-right', 'error', 'MicaChat', error.error?.message || 'Error al enviar mensaje');
        },
      });
    } else {
      this.metaApi.enviarMensaje(telefono.id, {
        para: para,
        texto: message,
        tipo: EMicaChat_TipoMensaje.TEXT,
      }).subscribe({
        error: (error) => {
          this.toastService.show('top-right', 'error', 'MicaChat', error.error?.message || 'Error al enviar mensaje');
        },
      });
    }
    this.clearMessage();
  }

  onComposerKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter') return;
    if (event.shiftKey) return;
    event.preventDefault();
    if (this.attachmentPreview()) {
      this.enviarAttachment();
      return;
    }
    this.sendMessage();
  }

  triggerFilePicker() {
    this.fileInputRef?.nativeElement?.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.mostrarPreviewArchivo(file);
    input.value = '';
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    this.mostrarPreviewArchivo(file);
  }

  // ─── Grabación de voz ───

  toggleRecording() {
    const state = this.recordingState();
    if (state === 'idle') {
      this.iniciarGrabacion();
    } else if (state === 'recording') {
      this.detenerGrabacion();
    }
  }

  // ─── Panel de previsualización unificado ───

  enviarAttachment() {
    const preview = this.attachmentPreview();
    if (!preview) return;

    const chatSeleccionado = this.MicaChatService.getChatSeleccionado();
    const telefonoSeleccionado = this.MicaChatService.getTelefonoSeleccionado();
    if (!chatSeleccionado || !telefonoSeleccionado) return;

    const formData = new FormData();
    formData.append('archivo', preview.file);
    formData.append('chatId', chatSeleccionado.id.toString());
    formData.append('telefonoNumero', telefonoSeleccionado.numero.toString());

    this.uploading.set(true);
    this.uploadProgress.set(0);

    const uploadService = preview.fromRecording
      ? this.metaApi.subirAudioGrabado(formData)
      : this.metaApi.subirArchivo(formData);

    uploadService.pipe(finalize(() => this.uploading.set(false))).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.uploadProgress.set(Math.round((event.loaded / event.total) * 100));
        } else if (event.type === HttpEventType.Response && event.body) {
          this.enviarMensajeConArchivo(
            telefonoSeleccionado,
            chatSeleccionado.numero_contacto,
            preview.tipo,
            event.body.url,
            preview.fileName,
          );
          this.cancelarAttachment();
          this.clearMessage();
        }
      },
      error: (err) => {
        this.toastService.show(
          'top-right',
          'error',
          'MicaChat',
          'Error al subir el archivo. Intenta de nuevo.',
        );
      },
    });
  }

  cancelarAttachment() {
    const preview = this.attachmentPreview();
    if (preview?.blobUrl) {
      URL.revokeObjectURL(preview.blobUrl);
    }
    this.attachmentPreview.set(null);
    this.recordingState.set('idle');
    this.tiempoGrabacion.set(0);
  }

  // ─── Métodos privados ───

  private mostrarPreviewArchivo(file: File) {
    if (file.size > TAMANO_MAX_MB * 1024 * 1024) {
      this.toastService.show(
        'top-right',
        'error',
        'MicaChat',
        `El archivo excede ${TAMANO_MAX_MB} MB`,
      );
      return;
    }
    const tipo = this.mapFileToTipo(file);
    const blobUrl = tipo === EMicaChat_TipoMensaje.IMAGE ? URL.createObjectURL(file) : null;

    this.revocarBlobUrl();
    this.attachmentPreview.set({ tipo, file, blobUrl, fileName: file.name, fromRecording: false });
  }

  private async iniciarGrabacion() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm; codecs=opus')
        ? 'audio/webm; codecs=opus'
        : 'audio/webm';
      this.audioMimeType = mimeType;

      this.mediaRecorder = new MediaRecorder(stream, { mimeType });
      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) this.audioChunks.push(e.data);
      };

      this.mediaRecorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
      };

      this.mediaRecorder.start();
      this.recordingState.set('recording');
      this.tiempoGrabacion.set(0);
      this.generateWaveform();

      this.grabacionInterval = setInterval(() => {
        this.tiempoGrabacion.update((t) => {
          if (t >= this.maxDuracionGrabacion) {
            this.detenerGrabacion();
            return this.maxDuracionGrabacion;
          }
          return t + 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Error al acceder al micrófono:', error);
      this.toastService.show('top-right', 'error', 'MicaChat', 'No se pudo acceder al micrófono');
    }
  }

  private detenerGrabacion() {
    if (this.grabacionInterval) {
      clearInterval(this.grabacionInterval);
      this.grabacionInterval = null;
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.audioChunks, { type: this.audioMimeType });
        this.audioChunks = [];
        const extension = this.audioMimeType.includes('webm') ? 'webm' : 'ogg';
        const fileName = `nota-de-voz-${Date.now()}.${extension}`;
        const blobUrl = URL.createObjectURL(blob);

        this.revocarBlobUrl();
        this.attachmentPreview.set({
          tipo: EMicaChat_TipoMensaje.AUDIO,
          file: new File([blob], fileName, { type: this.audioMimeType }),
          blobUrl,
          fileName,
          fromRecording: true,
        });
        this.recordingState.set('idle');

        setTimeout(() => {
          const audio = this.audioPreviewRef?.nativeElement;
          if (audio) {
            audio.src = blobUrl;
            audio.load();
          }
        }, 50);
      };
    } else {
      this.recordingState.set('idle');
    }
  }

  private enviarMensajeConArchivo(
    telefono: IMicaChatTelefonoData,
    para: string,
    tipo: EMicaChat_TipoMensaje,
    archivoUrl: string,
    archivoNombre: string,
  ) {
    const caption = this.messageDraft().trim() || '';

    const destino =
      telefono.proveedor === 'NO_OFICIAL'
        ? (this.MicaChatService.getChatSeleccionado()?.remoteJid || para)
        : para;

    const mensaje = {
      para: destino,
      texto: caption,
      archivo_url: archivoUrl,
      archivo_nombre: archivoNombre,
      tipo,
    };

    if (telefono.proveedor === 'NO_OFICIAL') {
      const evoDto: IEvolutionEnviarMensajeReq = {
        remoteJid: destino,
        texto: caption,
        tipo: tipo,
        archivoUrl: archivoUrl,
        archivoNombre: archivoNombre,
      };
      this.evoApi.enviarMensaje(telefono.id, evoDto).subscribe({
        next: () => this.uploadProgress.set(0),
        error: (error) => {
          this.toastService.show('top-right', 'error', 'MicaChat', error.error?.message || 'Error al enviar');
        },
      });
    } else {
      this.metaApi.enviarMensaje(telefono.id, mensaje).subscribe({
        next: () => this.uploadProgress.set(0),
        error: (error) => {
          this.toastService.show('top-right', 'error', 'MicaChat', error.error?.message || 'Error al enviar');
        },
      });
    }
  }

	private clearMessage() {
		this.messageDraft.set('');
		const textarea = this.getComposer();
		if (textarea) {
			textarea.value = '';
			this.autoResizeTextarea(textarea);
		}
		this.resetPresencia();
	}

	private resetPresencia() {
		if (this.typingTimeout) {
			clearTimeout(this.typingTimeout);
			this.typingTimeout = null;
		}
		this.lastPresenceSent = null;
		this.lastPresenceTime = 0;
		this.lastWordCount = 0;
	}

  private liberarRecursosGrabacion() {
    if (this.grabacionInterval) {
      clearInterval(this.grabacionInterval);
      this.grabacionInterval = null;
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
  }

  private revocarBlobUrl() {
    const current = this.attachmentPreview();
    if (current?.blobUrl) {
      URL.revokeObjectURL(current.blobUrl);
    }
  }

  private mapFileToTipo(file: File): EMicaChat_TipoMensaje {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (EXTENSIONES_IMAGEN.includes(ext)) return EMicaChat_TipoMensaje.IMAGE;
    if (EXTENSIONES_VIDEO.includes(ext)) return EMicaChat_TipoMensaje.VIDEO;
    if (EXTENSIONES_AUDIO.includes(ext)) return EMicaChat_TipoMensaje.AUDIO;
    if (EXTENSIONES_DOCUMENTO.includes(ext)) return EMicaChat_TipoMensaje.DOCUMENT;
    return EMicaChat_TipoMensaje.DOCUMENT;
  }

  private getComposer() {
    return this.composerRef?.nativeElement ?? null;
  }

  private insertAtCursor(textarea: HTMLTextAreaElement, text: string) {
    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    textarea.value = textarea.value.slice(0, start) + text + textarea.value.slice(end);
    const nextCursor = start + text.length;
    textarea.setSelectionRange(nextCursor, nextCursor);
  }

  private autoResizeTextarea(textarea: HTMLTextAreaElement) {
    textarea.style.height = 'auto';
    const nextHeight = Math.min(textarea.scrollHeight, this.composerMaxHeightPx);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > this.composerMaxHeightPx ? 'auto' : 'hidden';
  }

  formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  get MicaChat_TipoMensaje() {
    return EMicaChat_TipoMensaje;
  }

  permitidoTextoEnAttachment = computed(() => {
    const preview = this.attachmentPreview();
    if (!preview) return true;
    return (
      preview.tipo !== EMicaChat_TipoMensaje.DOCUMENT &&
      preview.tipo !== EMicaChat_TipoMensaje.AUDIO
    );
  });
}
